import { COLOURS, FONT_NAME, FONT_WEIGHT, SHADOWS, getBodyFont } from '@belong/themes';
import styled, { css } from 'styled-components';

const BORDER_RADIUS = '2.8rem';
const BORDER_WIDTH = (multiplier = 1): string => `${2 * multiplier}px`;

const preventStateChange = css`
  pointer-events: none;
`;

const hoverState = css`
  background-color: ${COLOURS.LIGHT_BLUE};
  border-color: ${COLOURS.LIGHT_BLUE};
  text-decoration: none;
  box-shadow: ${SHADOWS.AMBIENT_LIGHT};
`;

const selectedState = css`
  background-color: ${COLOURS.LIGHT_BLUE};
  border-color: ${COLOURS.BELONG_BLUE_DARK};
  text-decoration: none;
  font-weight: ${FONT_WEIGHT.SEMI_BOLD};
  padding: 1rem 1.15rem;
  color: ${COLOURS.DARK_BLUE};
`;

const focusedState = css`
  outline: none;

  &:after {
    position: absolute;
    content: '';
    top: ${BORDER_WIDTH(-2)};
    left: ${BORDER_WIDTH(-2)};
    width: calc(100% + ${BORDER_WIDTH(4)});
    height: calc(100% + ${BORDER_WIDTH(4)});
    border: ${BORDER_WIDTH(2)} solid ${COLOURS.BELONG_BLUE_DARK};
    border-radius: ${BORDER_RADIUS};
  }
`;

const disabledState = css`
  background-color: ${COLOURS.WHITE};
  border-color: ${COLOURS.GREY_200};
  font-weight: ${FONT_WEIGHT.REGULAR};
  color: ${COLOURS.GREY_400};
  ${preventStateChange};
`;

const baseChipStyles = css`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  min-width: 8rem;

  border: ${BORDER_WIDTH(1)} solid ${COLOURS.GREY_500};
  border-radius: ${BORDER_RADIUS};
  padding: 1rem 1.2rem;
  background-color: ${COLOURS.WHITE};

  color: ${COLOURS.GREY_600};
  font-family: ${FONT_NAME.PROXIMA_NOVA};
  font-weight: ${FONT_WEIGHT.REGULAR};

  ${getBodyFont('xs', 'medium')}

  cursor: pointer;
  user-select: none;
  transition:
    0.2s background-color ease,
    0.2s border-color ease;

  &:hover {
    ${hoverState}
  }
`;

// For direct use as a button
export const SimpleButtonChip = styled.button`
  ${baseChipStyles}
  display: flex;
  flex-direction: column;
  text-decoration: none;

  &:focus {
    ${focusedState};
  }
  &:disabled {
    ${disabledState};
  }
  &[aria-pressed='true'] {
    ${selectedState};
  }
`;

// For use with hidden inputs to create radio/checkbox
export const ButtonChip = styled.span`
  ${baseChipStyles}

  // push label down 1px
  > span {
    position: relative;
    top: 1px;
    white-space: nowrap; // prevent word-wrap for labels
  }

  input:focus ~ & {
    ${focusedState};
  }
  input:checked ~ & {
    ${selectedState};
  }
  input:disabled ~ & {
    ${disabledState};
  }
`;

export const HiddenInput = styled.input`
  position: absolute;
  top: auto;
  overflow: hidden;
  clip: rect(0.1rem, 0.1rem, 0.1rem, 0.1rem);
  width: 0.1rem;
  height: 0.1rem;
  white-space: nowrap;
`;
